import axios from "axios";
import moment from "moment";

const TRIP_HOST = "uk.trip.com";
const TRIP_ORIGIN = `https://${TRIP_HOST}`;
const TRIP_QUERY_ADDRESS_PATH = "/restapi/soa2/26393/queryAddress.json";
const TRIP_QUERY_FLIGHT_PATH = "/restapi/soa2/26393/queryFlight.json";
const TRIP_QUERY_PRODUCTS_PATH = "/restapi/soa2/26393/queryProducts.json";
const TRIP_DEFAULT_HEADERS = {
  Accept: "*/*",
  "Content-Type": "application/json",
  "User-Agent":
    "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/149.0.0.0 Safari/537.36",
};

class TripTransferService {
  constructor(config = {}) {
    this.headers = { ...TRIP_DEFAULT_HEADERS, ...config.headers };
  }

  _getClientId(existingClientId) {
    if (existingClientId) {
      return existingClientId;
    }

    const randomPart = Math.floor(Math.random() * 1e15)
      .toString()
      .padStart(15, "0");
    return `09${randomPart}`;
  }

  _buildTraceId(clientId, existingTraceId) {
    if (existingTraceId) {
      return existingTraceId;
    }

    const randomSuffix = Math.floor(Math.random() * 1e7);
    return `${clientId}-${Date.now()}-${randomSuffix}`;
  }

  _buildSoaUrl(path, clientId, traceId) {
    const query = new URLSearchParams({
      _fxpcqlniredt: clientId,
      "x-traceID": traceId,
    });

    return `${TRIP_ORIGIN}${path}?${query.toString()}`;
  }

  async _postSoa(url, payload, headers = this.headers) {
    try {
      const { data } = await axios.request({
        method: "post",
        maxBodyLength: Infinity,
        url,
        headers,
        data: payload,
      });
      return data;
    } catch (error) {
      console.log("error", JSON.stringify(error, null, 2));
      if (error.response) {
        throw new Error(`HTTP Error Status: ${error.response.status}`);
      }
      throw error;
    }
  }

  _buildProductsHeaders({ locale, currency }) {
    return {
      ...this.headers,
      Locale: locale,
      Currency: currency.toUpperCase(),
      Token: "",
      "x-ctx-country": "GB",
      "x-ctx-currency": currency.toUpperCase(),
      "x-ctx-locale": locale,
      Cookie: "",
    };
  }

  _buildProductsReqHead({
    clientId,
    locale,
    currency,
    pttype = 17,
    ptgroup = 17,
  }) {
    return {
      ...this._buildReqHead({ clientId, locale, currency, pttype, ptgroup }),
      ubt: {
        pageid: "10320677601",
        pvid: 2,
        sid: 1,
        vid: `${Date.now()}.1c97IJH56QJh`,
      },
    };
  }

  _buildFromAirportProductsPayload(options) {
    const {
      rideType,
      clientId,
      locale,
      currency,
      finalFlightDetails,
      location,
    } = options;
    return {
      reqhead: this._buildProductsReqHead({
        clientId,
        locale,
        currency,
        pttype: rideType === "from_airport" ? 17 : 18,
        ptgroup: rideType === "from_airport" ? 17 : 18,
      }),
      head: this._buildHead({ clientId, locale, currency }),
      biztype: 33,
      ptgroup: "17",
      pttype: "17",
      poicoordtype: "WGS84",
      version: 2,
      relationInfo: null,
      seo: false,
      versionCondition: {
        showUsingUrgentBooking: "true",
        rpPrice: 1,
        tieredCancelRule: "1",
      },
      ...finalFlightDetails,
      ...location,
    };
  }

  _buildToAirportProductsPayload(options) {
    const {
      clientId,
      locale,
      currency,
      pickupDateTime,
      airportCode,
      airportSubCode = "",
      cityId,
      adults,
      location,
      resolvedName,
      poiAddress,
      resolvedLat,
      resolvedLng,
    } = options;

    return {
      reqhead: this._buildReqHead({
        clientId,
        locale,
        currency,
        pttype: 18,
        ptgroup: 18,
      }),
      biztype: 33,
      ptgroup: "18",
      pttype: "18",
      stnno: "",
      stnadt: "",
      stnddt: "",
      stncd: airportCode,
      stnsubcd: airportSubCode,
      poinm: resolvedName,
      poiadr: poiAddress,
      poilat: String(resolvedLat),
      poilng: String(resolvedLng),
      poiref: location.poiRef,
      adult: String(adults),
      children: "0",
      stnnorefs: "",
      udt: pickupDateTime,
      stnid: "",
      poicoordtype: "WGS84",
      poicityid: String(cityId),
      version: 2,
      carPlaceId: location.carPlaceId || "",
      relationInfo: null,
      flightNo: "",
      flightTime: "",
      fromAirportCd: "",
      fromAirportNm: "",
      fromAirportSubnm: "",
      fromAirportCid: "0",
      toAirportCd: "",
      toAirportNm: "",
      toAirportSubnm: "",
      toAirportCid: "0",
      seo: false,
      versionCondition: {
        showUsingUrgentBooking: "true",
        rpPrice: 1,
        tieredCancelRule: "1",
      },
      head: this._buildHead({ clientId, locale, currency }),
    };
  }

  _buildReqHead({ clientId, locale, currency, pttype = 17, ptgroup = 17 }) {
    return {
      protocol: "http:",
      appid: "100013580",
      host: TRIP_HOST,
      locale,
      cury: currency.toLowerCase(),
      channelid: 14532,
      biztype: 33,
      pttype,
      ptgroup,
      sf: "trip_online",
      cid: clientId,
      os: "",
      mode: "web",
      gps: {},
      token: "",
      wlver: "0.202606091110",
      stntype: 0,
      union: {
        sid: "1621928",
        aid: "742331",
        ouid: "ctag.hash.uaJhHGIxPvn7",
        mktinfo: null,
      },
      ubt: {
        pageid: "10320677601",
        pvid: 2,
        sid: 4,
        vid: "1781513125127.2dbf4H1TVH9o",
      },
    };
  }

  _buildHead({ clientId, locale, currency }) {
    return {
      cid: clientId,
      ctok: "",
      cver: "1.0",
      lang: "01",
      sid: "8888",
      syscode: "09",
      auth: "",
      xsid: "",
      extension: [],
      Locale: locale,
      Language: "en",
      Currency: currency.toUpperCase(),
      ClientID: clientId,
    };
  }

  async fetchLocationTokens(options) {
    const {
      cityId,
      searchKey,
      locale = "en-GB",
      currency = "EUR",
      clientId: incomingClientId,
      traceId: incomingTraceId,
    } = options;

    const clientId = this._getClientId(incomingClientId);
    const traceId = this._buildTraceId(clientId, incomingTraceId);
    const url = this._buildSoaUrl(TRIP_QUERY_ADDRESS_PATH, clientId, traceId);

    const payload = {
      reqhead: this._buildReqHead({ clientId, locale, currency }),
      cid: cityId,
      key: searchKey.toLowerCase(),
      isNewPoiSearch: true,
      version: 1,
      head: this._buildHead({ clientId, locale, currency }),
    };

    const data = await this._postSoa(url, payload);
    console.log("location tokens data", JSON.stringify(data, null, 2));
    const status = data?.resstatus || {};
    if (status.rcode !== "200") {
      throw new Error(
        `API Business Logic Error: ${status.rmsg || "Unknown Error"}`,
      );
    }
    const pois = data.pois || [];
    return {
      clientId,
      traceId,
      pois,
    };
  }

  _selectBestFlight(flights, flightNo) {
    if (!flights?.length) {
      return null;
    }

    const normalizedFlightNo = flightNo?.trim().toUpperCase();
    if (normalizedFlightNo) {
      const exactMatch = flights.find(
        (flight) => flight.stnno?.trim().toUpperCase() === normalizedFlightNo,
      );
      if (exactMatch) {
        return exactMatch;
      }
    }

    return flights[0];
  }

  _mapFlightToDetails(flight, airports = {}, airlines = {}) {
    if (!flight) {
      return {
        stnNoRefs: "",
        stnId: "",
        flightNo: "",
        status: "",
        statusTips: "",
        departure: null,
        arrival: null,
        airline: null,
        bufferOptions: [],
      };
    }

    const airline = airlines[flight.aircd] || {};
    const departureAirport = airports[flight.dptstncd] || {};
    const arrivalAirport = airports[flight.arrstncd] || {};

    return {
      stnNoRefs: flight.stnref || "",
      stnId: flight.stnid || "",
      flightNo: flight.stnno || "",
      status: flight.statusnm || "",
      statusTips: flight.distips || "",
      departure: {
        airportCode: flight.dptstncd || "",
        airportName: departureAirport.nm || "",
        cityId: departureAirport.cid ?? null,
        scheduled: flight.dptplandt || "",
        actual: flight.dptdt || "",
        lat: flight.departLatLng?.latitude ?? null,
        lng: flight.departLatLng?.longitude ?? null,
      },
      arrival: {
        airportCode: flight.arrstncd || "",
        airportName: arrivalAirport.nm || "",
        cityId: arrivalAirport.cid ?? null,
        scheduled: flight.arrplandt || "",
        actual: flight.arrdt || "",
        lat: flight.arriveLatLng?.latitude ?? null,
        lng: flight.arriveLatLng?.longitude ?? null,
      },
      airline: {
        code: airline.cd || flight.aircd || "",
        name: airline.nm || "",
        logo: airline.logo || null,
      },
      bufferOptions: flight.bufferPop?.bufferList || [],
    };
  }

  async fetchFlightDetails(options) {
    const {
      flightNo,
      departureDate,
      isFuzzy = true,
      locale = "en-GB",
      currency = "EUR",
      clientId: incomingClientId,
      traceId: incomingTraceId,
    } = options;

    const clientId = this._getClientId(incomingClientId);
    const traceId = this._buildTraceId(clientId, incomingTraceId);
    const url = this._buildSoaUrl(TRIP_QUERY_FLIGHT_PATH, clientId, traceId);

    const payload = {
      reqhead: this._buildReqHead({ clientId, locale, currency }),
      dptdt: departureDate,
      fltno: flightNo,
      isFuzzy,
      head: this._buildHead({ clientId, locale, currency }),
    };

    const data = await this._postSoa(url, payload);
    const status = data?.resstatus || {};

    if (status.rcode !== "200") {
      throw new Error(
        `API Business Logic Error: ${status.rmsg || "Unknown Error"}`,
      );
    }

    const flights = data.list || [];
    const selectedFlight = this._selectBestFlight(flights, flightNo);

    return {
      ...this._mapFlightToDetails(
        selectedFlight,
        data.stn || {},
        data.airline || {},
      ),
      clientId,
      traceId,
      flights,
      airports: data.stn || {},
      cities: data.city || {},
      countries: data.country || {},
      airlines: data.airline || {},
    };
  }
  async getTransferRates(options) {
    const {
      rideType,
      clientId,
      traceId,
      flightNo,
      departureDate,
      fromAirportCd,
      locale = "en-GB",
      currency = "EUR",
      location,
      toAirportCd,
      pickupDateTime,
    } = options;

    let finalFlightDetails;
    if (rideType === "to_airport") {
      finalFlightDetails = {
        stnno: "",
        stnadt: "",
        stnddt: "",
        stncd: toAirportCd,
        stnsubcd: "", //still to figure out
        stnnorefs: "",
        flightNo: "",
        udt: moment(pickupDateTime).format("YYYY-MM-DD HH:mm"),
        stnid: "",
        flightTime: "",
        fromAirportCd: "",
        fromAirportNm: "",
        fromAirportSubnm: "",
        fromAirportCid: "0",
        toAirportCd: "",
        toAirportNm: "",
        toAirportSubnm: "",
        toAirportCid: "0",
      };
    } else {
      if (!departureDate) {
        throw new Error("departureDate is required for from-airport rides");
      }
      if (!flightNo) {
        throw new Error("flightNo is required for from-airport rides");
      }

      const flightDetails = await this.fetchFlightDetails({
        flightNo,
        departureDate,
        locale,
        currency,
        clientId,
        traceId,
      });
      const { flights, airports } = flightDetails;
      if (!flights?.length) {
        throw new Error(
          "No flights found for the given flightNo and departureDate",
        );
      }
      const targetFlight = flights[0];
      const resolvedStnNoRefs = targetFlight.stnref || flightDetails.stnNoRefs;
      if (!resolvedStnNoRefs) {
        throw new Error("stnNoRefs could not be resolved from flight details");
      }
      const fromAirportCityId =
        airports[targetFlight.dptstncd]?.cid || flightDetails.departure.cityId;
      const toAirportCityId =
        airports[targetFlight.arrstncd]?.cid || flightDetails.arrival.cityId;
      const flightActualDateTime =
        targetFlight.arrdt || flightDetails.arrival.actual;
      const flightScheduledDateTime =
        targetFlight.arrplandt || flightDetails.arrival.scheduled;
      const fromAirportName =
        airports[targetFlight.dptstncd]?.nm ||
        flightDetails.departure.airportName;
      const arrivalAirportName =
        airports[targetFlight.arrstncd]?.nm ||
        flightDetails.arrival.airportName;
      finalFlightDetails = {
        stnno: flightNo,
        stnadt: flightActualDateTime || flightScheduledDateTime,
        stnddt: flightScheduledDateTime,
        stncd: targetFlight.arrstncd,
        stnsubcd:
          targetFlight.arrstnsubcd || flightDetails.arrival.airportSubCode,
        stnnorefs: resolvedStnNoRefs,
        flightNo,
        udt: flightActualDateTime || flightScheduledDateTime,
        stnid: targetFlight.stnid || flightDetails.stnId,
        flightTime: flightActualDateTime || flightScheduledDateTime,
        fromAirportCd:
          targetFlight.dptstncd ||
          flightDetails.departure.airportCode ||
          fromAirportCd,
        fromAirportNm: fromAirportName,
        fromAirportSubnm:
          fromAirportName + " " + targetFlight?.dptTerminalShortName,
        fromAirportCid:
          typeof fromAirportCityId === "number"
            ? fromAirportCityId
            : Number(fromAirportCityId),
        toAirportCd: targetFlight.arrstncd || flightDetails.arrival.airportCode,
        toAirportNm: arrivalAirportName,
        toAirportSubnm:
          arrivalAirportName + " " + targetFlight?.arrTerminalShortName,
        toAirportCid:
          typeof toAirportCityId === "number"
            ? toAirportCityId
            : Number(toAirportCityId),
      };
    }

    const payload = this._buildFromAirportProductsPayload({
      rideType,
      clientId,
      locale,
      currency,
      finalFlightDetails,
      location,
    });

    const url = this._buildSoaUrl(
      TRIP_QUERY_PRODUCTS_PATH,
      location.clientId,
      location.traceId,
    );
    const rawData = await this._postSoa(
      url,
      payload,
      this._buildProductsHeaders({ locale, currency }),
    );

    const parsedRates = this._parseRelationalResponse(rawData, currency);

    return {
      ...parsedRates,
      rideType,
      ...(finalFlightDetails ? { finalFlightDetails } : {}),
    };
  }

  _parseRelationalResponse(apiData, selectedCurrency) {
    const status = apiData?.resstatus || {};
    if (status.rcode !== "200") {
      throw new Error(
        `API Business Logic Error: ${status.rmsg || "Unknown Error"}`,
      );
    }

    const result = apiData.result || {};
    const groups = result.grps || {};
    const vendors = result.vnds || {};
    const products = result.prds || {};
    const searchList = result.list || [];
    const distanceInfo = result.ext?.distance || {};

    const organizedCategories = searchList.map((item) => {
      const groupId = String(item.grpid);
      const associatedProductIds = item.prds || [];
      const groupConfig = groups[groupId] || {};

      const rates = associatedProductIds
        .map((prodId) => {
          const product = products[prodId] || {};
          const vendorId = String(product.vndid);
          const vendorConfig = vendors[vendorId] || {};

          return {
            productId: prodId,
            vendorName: vendorConfig.nm || "Independent Provider",
            vendorLogo: vendorConfig.img || null,
            price: parseFloat(product.amtshow || 0),
            currency: selectedCurrency.toUpperCase(),
            hasBaggageAssistance: product.svcs?.includes(400) || false,
          };
        })
        .sort((a, b) => a.price - b.price);

      return {
        categoryId: groupId,
        tierName: groupConfig.nm || "Standard Service",
        vehicleDescription: groupConfig.desc || "",
        capacity: {
          seats: groupConfig.seatdesc || "Max. 4",
          bags: groupConfig.bag || 2,
        },
        displayPriceFrom: groupConfig.minamt
          ? parseFloat(groupConfig.minamt)
          : null,
        availableRates: rates,
      };
    });

    return {
      routeSummary: {
        estimatedDuration: distanceInfo.minute
          ? `${distanceInfo.minute} mins`
          : "N/A",
        totalDistance: distanceInfo.tips || "",
      },
      fleetOffers: organizedCategories,
    };
  }
}

const tripClient = new TripTransferService();

const getTripSessionHeaders = (req) => ({
  clientId: req.headers._fxpcqlniredt,
  traceId: req.headers["x-traceid"],
});

export { TripTransferService };

export default {
  fetchLocationTokens: async (body = {}) => {
    const {
      cityId,
      searchKey,
      searchText,
      locale = "en-GB",
      currency = "EUR",
      clientId,
      traceId,
    } = body;

    const resolvedSearchKey =
      searchKey || searchText?.trim().toLowerCase().split(/\s+/)[0];

    if (!cityId || !resolvedSearchKey) {
      throw new Error("cityId and searchKey (or searchText) are required");
    }

    const tokens = await tripClient.fetchLocationTokens({
      cityId,
      searchKey: resolvedSearchKey,
      locale,
      currency,
      clientId,
      traceId,
    });

    return { data: tokens };
  },

  fetchFlightDetails: async (req, res) => {
    try {
      const {
        flightNo,
        departureDate,
        isFuzzy = true,
        locale = "en-GB",
        currency = "EUR",
      } = req.body;
      const { clientId, traceId } = getTripSessionHeaders(req);

      if (!flightNo || !departureDate) {
        return res.status(400).json({
          msg: "flightNo and departureDate are required",
        });
      }

      const data = await tripClient.fetchFlightDetails({
        flightNo,
        departureDate,
        isFuzzy,
        locale,
        currency,
        clientId,
        traceId,
      });

      return res.status(200).json({ data });
    } catch (error) {
      return res.status(500).json({
        msg: error.message || "Error fetching flight details",
      });
    }
  },

  getTransferRates: async (body = {}) => {
    const {
      rideType = "from_airport",
      flightNo,
      departureDate,
      fromAirportCd,
      locale = "en-GB",
      currency = "EUR",
      location,
      toAirportCd,
      pickupDateTime,
    } = body;

    if (rideType === "to_airport") {
      if (!pickupDateTime || !toAirportCd) {
        throw new Error(
          "pickupDateTime and toAirportCd are required for to-airport rides",
        );
      }
    } else if (!flightNo || !departureDate || !fromAirportCd) {
      throw new Error(
        "flightNo, departureDate and fromAirportCd are required for from-airport rides",
      );
    }

    if (!location) {
      throw new Error("location is required");
    }

    const data = await tripClient.getTransferRates({
      rideType,
      clientId: location.clientId,
      traceId: location.traceId,
      flightNo,
      departureDate,
      fromAirportCd,
      locale,
      currency,
      location,
      toAirportCd,
      pickupDateTime,
    });

    return { data };
  },
};
